/**
 * Centralized Logging System
 *
 * One pipeline: call site -> LogEntry -> filter -> redact -> LogTransport[].
 * Console is the only transport today. A remote sink is added later with
 * `addTransport(...)` and zero call-site changes. Redaction happens once,
 * before any transport sees the entry, so every transport is safe by
 * construction.
 *
 * @example
 * ```typescript
 * logger.info('User logged in', { user_id: 123 });
 * logger.error('API request failed', error, { endpoint: '/api/quizzes' });
 * logger.auth.info('Authentication successful', { method: 'jwt' });
 *
 * // Correlate all lines of one action
 * const log = logger.withRequest(requestId, LogLayer.ORCHESTRATOR);
 * log.info('Action started', { action });
 * ```
 */

import {
  Logger,
  BoundLogger,
  CategoryLogger,
  LogLevel,
  LogCategory,
  LogLayer,
  LoggerConfig,
  LogContext,
  LogEntry,
  LogError,
  LogTransport,
  Sensitive,
  REDACTED,
  SENSITIVE_KEYS,
  LOG_LEVEL_SEVERITY,
} from './logger.types';
import { getConfig, isDevelopment } from '@/config/environment';

export { LogLevel, LogCategory, LogLayer, markSensitive } from './logger.types';

/**
 * Default logger configuration
 * Uses centralized environment config
 */
const DEFAULT_CONFIG: Required<LoggerConfig> = {
  minLevel: LogLevel.DEBUG,
  categories: [],
  enableConsole: isDevelopment(),
};

let config: Required<LoggerConfig> = { ...DEFAULT_CONFIG };

// ---------------------------------------------------------------------------
// Redaction (applied once, in the pipeline)
// ---------------------------------------------------------------------------

const MAX_REDACT_DEPTH = 16;
const CYCLE_MARKER = '[CYCLE]';

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sensitiveKey =>
    lowerKey.includes(sensitiveKey.toLowerCase())
  );
}

/**
 * Deep-copies `data`, replacing sensitive keys and declared `Sensitive`
 * values with `[REDACTED]`. Never mutates input, never throws.
 */
function redactSensitiveData(
  data: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0
): unknown {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }
  if (data instanceof Sensitive) {
    return REDACTED;
  }
  if (depth >= MAX_REDACT_DEPTH || seen.has(data)) {
    return CYCLE_MARKER;
  }
  seen.add(data);

  let result: unknown;
  if (Array.isArray(data)) {
    result = data.map(item => redactSensitiveData(item, seen, depth + 1));
  } else if (data instanceof Error) {
    result = {
      name: data.name,
      message: data.message,
      stack: data.stack,
    };
  } else {
    const redacted: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      redacted[key] = isSensitiveKey(key)
        ? REDACTED
        : redactSensitiveData(
            (data as Record<string, unknown>)[key],
            seen,
            depth + 1
          );
    }
    result = redacted;
  }

  seen.delete(data);
  return result;
}

function safeRedactContext(context: LogContext): LogContext {
  try {
    return redactSensitiveData(context) as LogContext;
  } catch {
    return { redaction_error: true };
  }
}

// ---------------------------------------------------------------------------
// Entry construction
// ---------------------------------------------------------------------------

/**
 * Normalizes any thrown value to {name,message,stack}. The stack is always
 * present: non-Error values get a stack captured at the log site.
 */
function toLogError(error: unknown): LogError {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack || new Error(error.message).stack || '',
    };
  }

  let message: string;
  if (typeof error === 'string') {
    message = error;
  } else {
    try {
      message = JSON.stringify(redactSensitiveData(error));
    } catch {
      message = '[Unable to stringify error]';
    }
  }
  return {
    name: 'Error',
    message,
    stack: new Error(message).stack || '',
  };
}

interface Binding {
  request_id?: string;
  layer?: LogLayer;
}

function buildEntry(
  level: LogLevel,
  message: string,
  category: LogCategory | undefined,
  binding: Binding,
  context?: LogContext,
  error?: unknown
): LogEntry {
  const entry: LogEntry = {
    level,
    message,
    ts: Date.now(),
    layer: binding.layer ?? LogLayer.FRONTEND,
  };
  if (category) entry.category = category;
  if (binding.request_id) entry.request_id = binding.request_id;
  if (error !== undefined && error !== null) {
    entry.error = toLogError(error);
  }
  if (context && Object.keys(context).length > 0) {
    entry.context = safeRedactContext(context);
  }
  return entry;
}

// ---------------------------------------------------------------------------
// Transports
// ---------------------------------------------------------------------------

const transports: LogTransport[] = [];
let dispatching = false;

/**
 * Registers a transport (e.g. a future remote sink). Replaces any transport
 * with the same name. Entries are already filtered and redacted.
 */
export function addTransport(transport: LogTransport): void {
  removeTransport(transport.name);
  transports.push(transport);
}

/**
 * Unregisters a transport by name.
 */
export function removeTransport(name: string): void {
  const index = transports.findIndex(t => t.name === name);
  if (index >= 0) transports.splice(index, 1);
}

function shouldEmit(level: LogLevel, category?: LogCategory): boolean {
  if (LOG_LEVEL_SEVERITY[level] < LOG_LEVEL_SEVERITY[config.minLevel]) {
    return false;
  }
  if (config.categories.length > 0 && category) {
    return config.categories.includes(category);
  }
  return true;
}

/**
 * Hands the entry to every transport. A transport that throws or rejects is
 * swallowed; logging from inside a transport is dropped (no recursion).
 */
function dispatch(entry: LogEntry): void {
  if (dispatching || !shouldEmit(entry.level, entry.category)) {
    return;
  }
  dispatching = true;
  try {
    for (const transport of [...transports]) {
      try {
        const result = transport.write(entry);
        if (result && typeof result.catch === 'function') {
          result.catch(() => undefined);
        }
      } catch {
        // Transport failures never reach the caller.
      }
    }
  } finally {
    dispatching = false;
  }
}

const LOG_EMOJIS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '🔍',
  [LogLevel.INFO]: 'ℹ️',
  [LogLevel.WARN]: '⚠️',
  [LogLevel.ERROR]: '❌',
};

const CATEGORY_EMOJIS: Record<LogCategory, string> = {
  [LogCategory.AUTH]: '🔐',
  [LogCategory.API]: '🌐',
  [LogCategory.UI]: '🎨',
  [LogCategory.NETWORK]: '📡',
  [LogCategory.STORAGE]: '💾',
  [LogCategory.DATABASE]: '🗄️',
};

function formatDetails(entry: LogEntry): string {
  let out = '';
  if (entry.request_id) out += `\nrequest_id: ${entry.request_id}`;
  if (entry.error) {
    out += `\nError: ${entry.error.message}\nStack: ${entry.error.stack}`;
  }
  if (entry.context) {
    try {
      out += '\n' + JSON.stringify(entry.context, null, 2);
    } catch {
      out += '\n[Context formatting error]';
    }
  }
  return out;
}

/**
 * Console transport. Dev-only as before: production stays silent unless
 * `enableConsole` or the app debug flag is on.
 */
export const consoleTransport: LogTransport = {
  name: 'console',
  write(entry: LogEntry): void {
    if (!getConfig().enableDebug && !config.enableConsole) {
      return;
    }

    const date = new Date(entry.ts);
    const timestamp = Number.isNaN(date.getTime()) ? '' : date.toISOString();
    const emoji = LOG_EMOJIS[entry.level];
    const categoryEmoji = entry.category ? CATEGORY_EMOJIS[entry.category] : '';
    const categoryLabel = entry.category
      ? `[${entry.category.toUpperCase()}]`
      : '';
    const prefix =
      `${emoji} ${timestamp} ${categoryEmoji}${categoryLabel}`.trim();
    const line = `${prefix} ${entry.message}`;
    const details = formatDetails(entry);

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(line, details);
        break;
      case LogLevel.INFO:
        console.info(line, details);
        break;
      case LogLevel.WARN:
        console.warn(line, details);
        break;
      case LogLevel.ERROR:
        console.error(line, details);
        break;
    }
  },
};

addTransport(consoleTransport);

// ---------------------------------------------------------------------------
// Logger construction
// ---------------------------------------------------------------------------

function createCategoryLogger(
  category: LogCategory | undefined,
  binding: Binding
): CategoryLogger {
  const emit = (
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: unknown
  ): void => {
    try {
      dispatch(buildEntry(level, message, category, binding, context, error));
    } catch {
      // Logging must never throw into the caller.
    }
  };

  return {
    debug: (message, context) => emit(LogLevel.DEBUG, message, context),
    info: (message, context) => emit(LogLevel.INFO, message, context),
    warn: (message, context) => emit(LogLevel.WARN, message, context),
    error(message, error, context) {
      // Overloaded signature: error(message, context)
      if (error && typeof error === 'object' && !(error instanceof Error)) {
        emit(LogLevel.ERROR, message, error as LogContext);
      } else {
        emit(LogLevel.ERROR, message, context, error);
      }
    },
  };
}

function createBoundLogger(binding: Binding): BoundLogger {
  return {
    ...createCategoryLogger(undefined, binding),
    auth: createCategoryLogger(LogCategory.AUTH, binding),
    api: createCategoryLogger(LogCategory.API, binding),
    ui: createCategoryLogger(LogCategory.UI, binding),
    network: createCategoryLogger(LogCategory.NETWORK, binding),
    storage: createCategoryLogger(LogCategory.STORAGE, binding),
    database: createCategoryLogger(LogCategory.DATABASE, binding),
  };
}

function createLogger(): Logger {
  const root = createBoundLogger({});
  const logger: Logger = {
    ...root,
    withRequest(requestId: string, layer?: LogLayer): BoundLogger {
      return createBoundLogger({ request_id: requestId, layer });
    },
    configure(newConfig: LoggerConfig): void {
      config = { ...config, ...newConfig };
      root.info('Logger configuration updated', {
        minLevel: config.minLevel,
        categories: config.categories,
        enableConsole: config.enableConsole,
      });
    },
  };
  return logger;
}

/**
 * Singleton logger instance
 */
export const logger: Logger = createLogger();

/**
 * New client-side correlation id (one per action execution).
 */
export function generateRequestId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Resets config and transports to defaults. Test-only.
 */
export function resetLoggerForTests(): void {
  config = { ...DEFAULT_CONFIG };
  transports.length = 0;
  addTransport(consoleTransport);
  dispatching = false;
}

export type {
  Logger,
  BoundLogger,
  LoggerConfig,
  LogContext,
  LogEntry,
  LogTransport,
};
